export type User = {
  id: string;
  username: string;
  display_name: string;
  role: string;
  status: string;
};

export type Resource = {
  id: string;
  type: 'directory' | 'file' | 'external_link' | 'collection';
  status: string;
  title: string;
  description: string;
  path: string;
  parent_path: string;
  name: string;
  size: number;
  file_count: number;
  modified_at: string;
  extension: string;
  mime_type: string;
  is_root: boolean;
  preview_url: string | null;
  workspace_preview_url: string | null;
  embed_preview_url: string | null;
  thumbnail_url: string | null;
  download_url: string | null;
  raw_url: string | null;
  children_url: string | null;
  can_have_child: boolean;
  can_upload_into: boolean;
};

export type PreviewInfo = {
  resource_id: string;
  status: string;
  kind: string;
  thumbnail_url: string;
  embed_preview_url: string;
  pages_url: string;
  full_preview_url: string;
  legacy_preview_url: string;
};

export type PreviewPageInfo = {
  page: number;
  url: string;
};

export type PreviewPagesInfo = {
  status: string;
  kind: string;
  page_count: number;
  pages: PreviewPageInfo[];
  fallback_url: string;
  source_signature: string;
};

export type Task = {
  id: string;
  type: 'thumbnail' | 'preview_prewarm' | 'search_extract';
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  resource_id: string;
  priority: number;
  payload: Record<string, unknown>;
  result: Record<string, unknown>;
  error: string;
  attempts: number;
  created_at: string;
  updated_at: string;
};

export type KnowledgeTag = {
  id: string;
  name: string;
  slug: string;
  color: string;
  created_at: string;
  updated_at: string;
};

export type ExternalLinkInfo = {
  resource: Resource;
  url: string;
  domain: string;
  favicon_url: string;
  embed_allowed: boolean;
  target: '_blank';
  rel: 'noopener noreferrer';
};

export type CollectionInfo = {
  resource: Resource;
  item_count: number;
};

export type CollectionItem = {
  id: string;
  collection_resource_id: string;
  resource_id: string;
  position: number;
  resource: Resource;
};

export type SearchResult = {
  resource: Resource;
  index_status: string;
  snippet: string;
  rank: number;
  tags: KnowledgeTag[];
  favorite: boolean;
  external_link?: ExternalLinkInfo;
  collection?: CollectionInfo;
  opened_at?: string;
};

export type SearchStatus = {
  total: number;
  ready: number;
  queued: number;
  indexing: number;
  failed: number;
  stale: number;
};

export type TrashItem = {
  id: string;
  batch_id: string;
  resource_id: string;
  status: 'trashed' | 'restored' | 'purged';
  original_path: string;
  original_title: string;
  resource_type: string;
  size: number;
  deleted_at: string;
  expires_at: string;
  restored_at?: string;
  purged_at?: string;
  resource: Resource;
};

export type ShareInfo = {
  id: string;
  root_resource_id: string;
  allow_preview: boolean;
  allow_download: boolean;
  allow_zip_download: boolean;
  requires_password: boolean;
  expires_at: string;
  created_at: string;
  updated_at: string;
  revoked_at?: string;
  resource: Resource;
  token?: string;
  url?: string;
};

export type PublicShareInfo = {
  id: string;
  allow_preview: boolean;
  allow_download: boolean;
  requires_password: boolean;
  expires_at: string;
};

export type PublicShareResponse =
  | {
      locked: true;
      requires_password: true;
    }
  | {
      locked?: false;
      share: PublicShareInfo;
      resource: Resource;
      children: Resource[];
    };

export type ThemeSettings = {
  mode: string;
  accent: string;
  background_blur: number;
  background_dim: number;
  surface_opacity: number;
  default_view: string;
  has_background: boolean;
  background_url: string;
  background_size: number;
  updated_at: string;
};

export type ThemeBackgroundUpload = {
  background_size: number;
  background_mime_type: string;
  uploaded_at: string;
};

export type ThemePreferencesInput = {
  mode: string;
  accent: string;
  background_blur: number;
  background_dim: number;
  surface_opacity: number;
  default_view: string;
};

const themeBackgroundUploadTimeoutMs = 10 * 60 * 1000;
const themeSaveTimeoutMs = 20 * 1000;
const themeRecoverTimeoutMs = 8 * 1000;

type APIErrorPayload = {
  ok: false;
  error?: {
    code?: string;
    message?: string;
  };
};

export class APIError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init?.headers ?? {})
    },
    ...init
  });
  const data = (await response.json().catch(() => null)) as T | APIErrorPayload | null;
  if (!response.ok) {
    const payload = data as APIErrorPayload | null;
    throw new APIError(
      response.status,
      payload?.error?.code ?? 'request_failed',
      payload?.error?.message ?? '请求失败'
    );
  }
  return data as T;
}

function csrfToken(): string {
  return document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('janestra_v1_csrf='))
    ?.split('=')
    .slice(1)
    .join('=') ?? '';
}

export async function me(): Promise<User> {
  const data = await request<{ ok: true; user: User }>('/api/v1/me');
  return data.user;
}

export async function login(username: string, password: string): Promise<User> {
  const data = await request<{ ok: true; user: User; csrf_token: string }>('/api/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password })
  });
  return data.user;
}

export async function logout(): Promise<void> {
  await request<{ ok: true }>('/api/v1/auth/logout', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken() }
  });
}

export async function listResources(params: { parentId?: string; parentPath?: string } = {}): Promise<{
  parent_path: string;
  resources: Resource[];
}> {
  const query = new URLSearchParams();
  if (params.parentId) query.set('parent_id', params.parentId);
  if (params.parentPath) query.set('parent_path', params.parentPath);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await request<{ ok: true; parent_path: string; resources: Resource[] }>(`/api/v1/resources${suffix}`);
  return { parent_path: data.parent_path, resources: data.resources };
}

export async function getResourcePreview(resourceId: string): Promise<{ resource: Resource; preview: PreviewInfo }> {
  const data = await request<{ ok: true; resource: Resource; preview: PreviewInfo }>(`/api/v1/resources/${resourceId}/preview`);
  return { resource: data.resource, preview: data.preview };
}

export async function getResourcePreviewPages(resourceId: string): Promise<{ resource: Resource; pages: PreviewPagesInfo }> {
  const data = await request<{ ok: true; resource: Resource; pages: PreviewPagesInfo }>(`/api/v1/resources/${resourceId}/preview-pages`);
  return { resource: data.resource, pages: data.pages };
}

export async function createFolder(input: { parentId?: string; parentPath?: string; name: string }): Promise<Resource> {
  const data = await request<{ ok: true; resource: Resource }>('/api/v1/resources/folders', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ parent_id: input.parentId ?? '', parent_path: input.parentPath ?? '', name: input.name })
  });
  return data.resource;
}

export async function uploadResource(input: { parentId?: string; parentPath?: string; file: File }): Promise<Resource> {
  const body = new FormData();
  if (input.parentId) body.append('parent_id', input.parentId);
  if (input.parentPath) body.append('parent_path', input.parentPath);
  body.append('file', input.file);
  const data = await request<{ ok: true; resource: Resource }>('/api/v1/resources/upload', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken() },
    body
  });
  return data.resource;
}

export function zipDownloadName(name: string): string {
  const trimmed = name.trim() || '资料下载';
  return trimmed.toLowerCase().endsWith('.zip') ? trimmed : `${trimmed}.zip`;
}

export function currentFolderZipURL(input: { parentId?: string; parentPath?: string }): string {
  const query = new URLSearchParams();
  if (input.parentId) query.set('parent_id', input.parentId);
  if (input.parentPath) query.set('parent_path', input.parentPath);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return `/api/v1/resources/zip${suffix}`;
}

export function resourcesZipURL(input: { resourceIds: string[]; name?: string }): string {
  const query = new URLSearchParams();
  input.resourceIds.forEach((id) => query.append('resource_id', id));
  if (input.name) query.set('name', input.name);
  return `/api/v1/resources/zip?${query.toString()}`;
}

export async function getThemeSettings(options?: { signal?: AbortSignal }): Promise<ThemeSettings> {
  const data = await request<{ ok: true; theme: ThemeSettings }>('/api/v1/theme', { signal: options?.signal });
  return data.theme;
}

export async function uploadThemeBackground(background: Blob): Promise<ThemeBackgroundUpload> {
  const body = new FormData();
  body.append('background', background, `background.${themeImageExtension(background.type)}`);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), themeBackgroundUploadTimeoutMs);
  try {
    const data = await request<{ ok: true; upload: ThemeBackgroundUpload }>('/api/v1/theme/background-upload', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
      body,
      signal: controller.signal
    });
    return data.upload;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new APIError(0, 'request_timeout', '背景上传超时，请检查网络后重试');
    }
    throw err;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function saveThemeSettings(input: {
  preferences: ThemePreferencesInput;
  background?: Blob | null;
  useUploadedBackground?: boolean;
  uploadedBackgroundSize?: number;
  removeBackground?: boolean;
}): Promise<ThemeSettings> {
  const body = new FormData();
  body.append('preferences', JSON.stringify(input.preferences));
  if (input.removeBackground) body.append('remove_background', '1');
  if (input.useUploadedBackground) body.append('use_uploaded_background', '1');
  if (input.background) body.append('background', input.background, `background.${themeImageExtension(input.background.type)}`);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), themeSaveTimeoutMs);
  try {
    const data = await request<{ ok: true; theme: ThemeSettings }>('/api/v1/theme', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken() },
      body,
      signal: controller.signal
    });
    return data.theme;
  } catch (err) {
    if (isRecoverableThemeSaveError(err)) {
      const recovered = await recoverThemeSave(input);
      if (recovered) return recovered;
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new APIError(0, 'request_timeout', '保存确认超时，请刷新设置页确认是否已保存；背景图上传成功后不会因为确认超时丢失');
    }
    throw err;
  } finally {
    window.clearTimeout(timeout);
  }
}

function isRecoverableThemeSaveError(err: unknown) {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  return err instanceof APIError && (err.status === 0 || err.status === 408 || err.status >= 500);
}

async function recoverThemeSave(input: {
  preferences: ThemePreferencesInput;
  background?: Blob | null;
  useUploadedBackground?: boolean;
  uploadedBackgroundSize?: number;
  removeBackground?: boolean;
}) {
  await delay(1600);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), themeRecoverTimeoutMs);
  try {
    const theme = await getThemeSettings({ signal: controller.signal }).catch(() => null);
    if (!theme || !themeMatchesSaveInput(theme, input)) return null;
    return theme;
  } finally {
    window.clearTimeout(timeout);
  }
}

function themeMatchesSaveInput(theme: ThemeSettings, input: {
  preferences: ThemePreferencesInput;
  background?: Blob | null;
  useUploadedBackground?: boolean;
  uploadedBackgroundSize?: number;
  removeBackground?: boolean;
}) {
  const preferences = input.preferences;
  if (
    theme.mode !== preferences.mode ||
    theme.accent.toLowerCase() !== preferences.accent.toLowerCase() ||
    theme.background_blur !== preferences.background_blur ||
    theme.background_dim !== preferences.background_dim ||
    theme.surface_opacity !== preferences.surface_opacity ||
    theme.default_view !== preferences.default_view
  ) {
    return false;
  }
  if (input.removeBackground) return !theme.has_background;
  if (input.useUploadedBackground) {
    if (!theme.has_background || theme.background_size <= 0) return false;
    return !input.uploadedBackgroundSize || theme.background_size === input.uploadedBackgroundSize;
  }
  if (input.background) return theme.has_background && theme.background_size > 0;
  return true;
}

function delay(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

function themeImageExtension(mimeType: string) {
  switch (mimeType) {
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/gif':
      return 'gif';
    default:
      return 'jpg';
  }
}

export async function listTasks(): Promise<Task[]> {
  const data = await request<{ ok: true; tasks: Task[] }>('/api/v1/tasks');
  return data.tasks;
}

export async function prewarmTasks(): Promise<Record<string, number>> {
  const data = await request<{ ok: true; queued: Record<string, number> }>('/api/v1/tasks/prewarm', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
  return data.queued;
}

export async function searchResources(params: {
  q?: string;
  type?: string;
  tagId?: string;
  collectionId?: string;
  favorite?: boolean;
  limit?: number;
} = {}): Promise<SearchResult[]> {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.type) query.set('type', params.type);
  if (params.tagId) query.set('tag_id', params.tagId);
  if (params.collectionId) query.set('collection_id', params.collectionId);
  if (params.favorite) query.set('favorite', '1');
  if (params.limit) query.set('limit', String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const data = await request<{ ok: true; results: SearchResult[] }>(`/api/v1/search${suffix}`);
  return data.results;
}

export async function getSearchStatus(): Promise<SearchStatus> {
  const data = await request<{ ok: true; status: SearchStatus }>('/api/v1/search/status');
  return data.status;
}

export async function listTags(): Promise<KnowledgeTag[]> {
  const data = await request<{ ok: true; tags: KnowledgeTag[] }>('/api/v1/tags');
  return data.tags;
}

export async function createTag(name: string, color = ''): Promise<KnowledgeTag> {
  const data = await request<{ ok: true; tag: KnowledgeTag }>('/api/v1/tags', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, color })
  });
  return data.tag;
}

export async function createExternalLink(input: { title: string; url: string; description?: string }): Promise<ExternalLinkInfo> {
  const data = await request<{ ok: true; link: ExternalLinkInfo }>('/api/v1/links', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return data.link;
}

export async function listCollections(): Promise<CollectionInfo[]> {
  const data = await request<{ ok: true; collections: CollectionInfo[] }>('/api/v1/collections');
  return data.collections;
}

export async function createCollection(input: { title: string; description?: string }): Promise<CollectionInfo> {
  const data = await request<{ ok: true; collection: CollectionInfo }>('/api/v1/collections', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return data.collection;
}

export async function addCollectionItem(collectionId: string, resourceId: string): Promise<CollectionItem> {
  const data = await request<{ ok: true; item: CollectionItem }>(`/api/v1/collections/${collectionId}/items`, {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ resource_id: resourceId })
  });
  return data.item;
}

export async function setFavorite(resourceId: string, favorite: boolean): Promise<void> {
  await request<{ ok: true }>(`/api/v1/resources/${resourceId}/favorite`, {
    method: favorite ? 'POST' : 'DELETE',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
}

export async function recordRecent(resourceId: string, eventType: 'open' | 'preview' | 'download' = 'open'): Promise<void> {
  try {
    await request<{ ok: true }>(`/api/v1/resources/${resourceId}/recent`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: eventType })
    });
  } catch {
    // 最近记录是尽力而为的埋点；游客态没有会话可记录，失败不打断阅读。
  }
}

export async function listFavorites(): Promise<SearchResult[]> {
  const data = await request<{ ok: true; results: SearchResult[] }>('/api/v1/favorites?limit=8');
  return data.results;
}

export async function listRecent(): Promise<SearchResult[]> {
  const data = await request<{ ok: true; results: SearchResult[] }>('/api/v1/recent?limit=8');
  return data.results;
}

export async function deleteResource(resourceId: string): Promise<TrashItem> {
  const data = await request<{ ok: true; trash_item: TrashItem }>(`/api/v1/resources/${resourceId}`, {
    method: 'DELETE',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
  return data.trash_item;
}

export async function listTrash(): Promise<TrashItem[]> {
  const data = await request<{ ok: true; trash: TrashItem[] }>('/api/v1/trash');
  return data.trash;
}

export async function restoreTrash(trashId: string): Promise<TrashItem> {
  const data = await request<{ ok: true; trash_item: TrashItem }>(`/api/v1/trash/${trashId}/restore`, {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
  return data.trash_item;
}

export async function purgeTrash(trashId: string): Promise<void> {
  await request<{ ok: true }>(`/api/v1/trash/${trashId}`, {
    method: 'DELETE',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
}

export async function cleanupTrash(): Promise<{ removed: number; failed: number }> {
  const data = await request<{ ok: true; removed: number; failed: number }>('/api/v1/trash/cleanup', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
  return { removed: data.removed, failed: data.failed };
}

export async function listShares(): Promise<ShareInfo[]> {
  const data = await request<{ ok: true; shares: ShareInfo[] }>('/api/v1/shares');
  return data.shares;
}

export async function createShare(input: {
  resource_id: string;
  password?: string;
  allow_preview: boolean;
  allow_download: boolean;
  allow_zip_download?: boolean;
  expires_at: string;
}): Promise<ShareInfo> {
  const data = await request<{ ok: true; share: ShareInfo }>('/api/v1/shares', {
    method: 'POST',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return data.share;
}

export async function revokeShare(shareId: string): Promise<void> {
  await request<{ ok: true }>(`/api/v1/shares/${shareId}`, {
    method: 'DELETE',
    headers: { 'X-CSRF-Token': csrfToken(), 'Content-Type': 'application/json' },
    body: JSON.stringify({})
  });
}

export async function getPublicShare(token: string, unlockToken = ''): Promise<PublicShareResponse> {
  const query = new URLSearchParams();
  if (unlockToken) query.set('unlock_token', unlockToken);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const response = await fetch(`/api/v1/public/shares/${encodeURIComponent(token)}${suffix}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' }
  });
  const data = (await response.json().catch(() => null)) as
    | ({ ok: true; locked: true; requires_password: true } | { ok: true; share: PublicShareInfo; resource: Resource; children: Resource[] })
    | APIErrorPayload
    | null;
  if (response.status === 423 && data && 'locked' in data && data.locked) {
    return { locked: true, requires_password: true };
  }
  if (!response.ok) {
    const payload = data as APIErrorPayload | null;
    throw new APIError(
      response.status,
      payload?.error?.code ?? 'share_not_found',
      payload?.error?.message ?? '分享不可用'
    );
  }
  const payload = data as { ok: true; share: PublicShareInfo; resource: Resource; children: Resource[] };
  return { share: payload.share, resource: payload.resource, children: payload.children };
}

export async function unlockPublicShare(token: string, password: string): Promise<string> {
  const data = await request<{ ok: true; unlock_token: string }>(`/api/v1/public/shares/${encodeURIComponent(token)}/unlock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password })
  });
  return data.unlock_token;
}

export function publicPreviewURL(token: string, resourceId: string, unlockToken = ''): string {
  const query = new URLSearchParams();
  if (unlockToken) query.set('unlock_token', unlockToken);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return `/api/v1/public/shares/${encodeURIComponent(token)}/resources/${encodeURIComponent(resourceId)}/preview${suffix}`;
}

export function publicDownloadURL(token: string, resourceId: string, unlockToken = ''): string {
  const query = new URLSearchParams();
  if (unlockToken) query.set('unlock_token', unlockToken);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return `/api/v1/public/shares/${encodeURIComponent(token)}/resources/${encodeURIComponent(resourceId)}/download${suffix}`;
}
